import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, User, Phone, Send, CheckCircle, Bell } from 'lucide-react';
import apiService from '../services/api';
import './NewsletterSignup.css';

const NewsletterSignup = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Send data to backend API
      await apiService.subscribeToNewsletter(
        formData.fullName,
        formData.email,
        formData.phone
      );

      setIsSubmitted(true);
      setFormData({ fullName: '', email: '', phone: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      // Show error message to user
      setErrors({ submit: error.message || 'Failed to subscribe. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div 
        className="newsletter-success-modern"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="success-icon-container">
          <CheckCircle className="success-icon" />
        </div>
        <h3>Successfully Subscribed!</h3>
        <p>Thank you for signing up. We'll notify you about new opportunities in NYC.</p>
        <button 
          className="btn-resubscribe"
          onClick={() => setIsSubmitted(false)}
        >
          Subscribe Another Email
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="newsletter-signup-modern"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="newsletter-header">
        <div className="newsletter-icon">
          <Bell className="w-6 h-6" />
        </div>
        <h3>Get Job Alerts</h3>
        <p>Join 5,000+ NYC workers getting exclusive job alerts and tips</p>
      </div>

      <form onSubmit={handleSubmit} className="newsletter-form">
        <div className="form-group">
          <div className="input-wrapper">
            <User className="input-icon" />
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Full Name"
              className={`form-input ${errors.fullName ? 'error' : ''}`}
            />
          </div>
          {errors.fullName && <span className="error-message">{errors.fullName}</span>}
        </div>

        <div className="form-group">
          <div className="input-wrapper">
            <Mail className="input-icon" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email Address"
              className={`form-input ${errors.email ? 'error' : ''}`}
            />
          </div>
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className="form-group">
          <div className="input-wrapper">
            <Phone className="input-icon" />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Phone Number"
              className={`form-input ${errors.phone ? 'error' : ''}`}
            />
          </div>
          {errors.phone && <span className="error-message">{errors.phone}</span>}
        </div>

        {errors.submit && (
          <div className="error-message submit-error">{errors.submit}</div>
        )}

        <button 
          type="submit" 
          className="newsletter-submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="loading-spinner"></div>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Subscribe Now
            </>
          )}
        </button>
      </form>

      <div className="newsletter-benefits">
        <div className="benefit-item">
          <CheckCircle className="w-4 h-4" />
          <span>Get notified first about new opportunities</span>
        </div>
        <div className="benefit-item">
          <CheckCircle className="w-4 h-4" />
          <span>Exclusive access to high-paying gigs</span>
        </div>
        <div className="benefit-item">
          <CheckCircle className="w-4 h-4" />
          <span>Weekly updates on available positions</span>
        </div>
      </div>
    </motion.div>
  );
};

export default NewsletterSignup; 